import React, { useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal, LineReveal } from "@/components/Reveal";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { SERVICES, TEAM, IMAGES, COMPANY } from "@/lib/data";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden bg-background">
      <motion.div style={{ y }} className="absolute inset-0 z-0">
        <img src={IMAGES.heroAbstract} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/80 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity }} className="container-edge relative z-10 pb-24 pt-40">
        <h1 className="font-display font-light text-primary text-[13vw] leading-[0.92] md:text-[8.5vw] tracking-tight">
          <LineReveal lines={["Securing Ideas.", "Empowering"]} delay={0.3} />
          <span className="reveal-line-wrap">
            <motion.span className="block italic text-accent" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.54 }}>
              Innovation.
            </motion.span>
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8"
        >
          <p className="max-w-xl text-lg text-foreground/70 leading-relaxed">
            A premium intellectual property management &amp; consulting firm specialising in IP audit, valuation, and
            portfolio strategy — from trademark registration to complex IP litigation.
          </p>
          <div className="flex gap-4">
            <Link data-testid="hero-services-btn" to="/#services" className="btn-primary">Our Services</Link>
            <Link data-testid="hero-contact-btn" to="/contact" className="btn-secondary">Consult Us</Link>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

const MarqueeRibbon = () => (
  <div data-testid="marquee-ribbon" className="bg-primary text-primary-foreground py-6 border-y border-primary">
    <Marquee speed={38} gradient={false} autoFill>
      {["Trademarks", "Patents", "Copyrights", "Designs", "IP Litigation", "IP Audit", "Valuation", "Portfolio Management"].map((t, i) => (
        <span key={i} className="font-display italic text-3xl md:text-4xl tracking-wide mx-8 text-primary-foreground/90">
          {t} <span className="text-accent mx-6">•</span>
        </span>
      ))}
    </Marquee>
  </div>
);

const Manifesto = () => {
  const chapters = [
    { n: "01", t: "Who We Are", b: "IPNOMICS LLP is a specialist intellectual property firm born from a simple conviction — that ideas are the most valuable assets an organisation owns. We combine legal rigour with commercial insight to protect and grow them." },
    { n: "02", t: "What We Do", b: "We audit, value, protect, and enforce intellectual property. From the first trademark filing to the most complex litigation, our focus remains on turning intangible rights into measurable, defensible business value." },
  ];
  return (
    <section id="about" data-testid="manifesto-section" className="bg-background py-24 md:py-36 border-b border-border">
      <div className="container-edge">
        <Reveal><p className="overline mb-16">The Firm</p></Reveal>
        <Reveal className="mb-24 overflow-hidden border border-border">
          <img src={IMAGES.office} alt="IPNOMICS LLP office" className="w-full h-[320px] md:h-[440px] object-cover grayscale hover:grayscale-0 transition-all duration-700" />
        </Reveal>
        <div className="space-y-24">
          {chapters.map((c, i) => (
            <div key={c.n} className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start">
              <Reveal className="md:col-span-4" delay={i * 0.05}>
                <div className="font-display italic text-8xl md:text-[9rem] leading-none text-accent/90">{c.n}</div>
              </Reveal>
              <div className="md:col-span-8 md:pt-6">
                <Reveal delay={0.1}>
                  <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight text-primary mb-6">{c.t}</h2>
                  <p className="text-lg md:text-xl text-foreground/70 leading-relaxed max-w-3xl">{c.b}</p>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ServiceCard = ({ s }) => (
  <Reveal
    className={`group relative border border-border bg-card p-8 flex flex-col justify-between overflow-hidden transition-colors duration-500 hover:border-accent ${
      s.star ? "md:col-span-8 md:row-span-2 bg-primary text-primary-foreground border-primary min-h-[340px]" : "md:col-span-4 min-h-[220px]"
    }`}
  >
    <div>
      <span className={`overline ${s.star ? "text-accent" : "text-accent"}`}>{s.tag}</span>
      <h3 className={`font-display font-light tracking-tight mt-4 ${s.star ? "text-5xl md:text-6xl" : "text-3xl"}`}>{s.title}</h3>
    </div>
    {s.star && (
      <div className="my-8 overflow-hidden border border-white/10">
        <img src={IMAGES.document} alt="Intellectual property documentation" className="w-full h-40 md:h-52 object-cover opacity-85 grayscale group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700" />
      </div>
    )}
    <p className={`mt-6 leading-relaxed ${s.star ? "text-primary-foreground/70 text-lg max-w-lg" : "text-foreground/65 text-sm"}`}>
      {s.desc}
    </p>
    <ArrowUpRight className={`absolute top-8 right-8 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 ${s.star ? "text-accent" : "text-foreground/30 group-hover:text-accent"}`} size={s.star ? 32 : 22} />
  </Reveal>
);

const Services = () => (
  <section id="services" data-testid="services-section" className="bg-secondary/40 py-24 md:py-36 border-b border-border">
    <div className="container-edge">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
        <Reveal>
          <p className="overline mb-4">Practice Areas</p>
          <h2 className="font-display font-light text-4xl md:text-6xl tracking-tight text-primary max-w-2xl">Full-spectrum intellectual property counsel.</h2>
        </Reveal>
        <Reveal delay={0.1}><Link to="/contact" className="btn-secondary shrink-0">Request Proposal</Link></Reveal>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 auto-rows-auto">
        {SERVICES.map((s) => <ServiceCard key={s.id} s={s} />)}
      </div>
    </div>
  </section>
);

const Team = () => (
  <section id="team" data-testid="team-section" className="bg-background py-24 md:py-36 border-b border-border">
    <div className="container-edge">
      <Reveal><p className="overline mb-4">The Partners</p>
        <h2 className="font-display font-light text-4xl md:text-6xl tracking-tight text-primary mb-16 max-w-2xl">Seasoned counsel behind every mandate.</h2>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {TEAM.map((m, i) => (
          <Reveal key={m.name} delay={i * 0.1} className="group relative border border-border overflow-hidden">
            <div className="overflow-hidden">
              <img src={m.image} alt={m.name} style={{ objectPosition: m.pos || "50% 0%" }} className="w-full h-[460px] object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105" />
            </div>
            <div className="p-8 bg-card">
              <h3 className="font-display text-3xl text-primary">{m.name}</h3>
              <p className="overline mt-2 mb-4">{m.role}</p>
              <p className="text-foreground/70 leading-relaxed">{m.bio}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ContactCTA = () => (
  <section id="contact-cta" data-testid="home-contact-cta" className="bg-primary text-primary-foreground relative overflow-hidden">
    <div className="grain absolute inset-0" />
    <div className="container-edge relative py-24 md:py-32 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div>
        <Reveal><p className="overline mb-6 text-accent">Let&apos;s Talk</p>
          <h2 className="font-display font-light text-4xl md:text-6xl tracking-tight leading-tight mb-8">
            Protect what makes<br /><span className="italic text-accent">your business</span> unique.
          </h2>
          <Link data-testid="cta-contact-btn" to="/contact" className="inline-flex items-center gap-2 bg-accent text-white px-8 py-4 text-xs font-medium tracking-[0.18em] uppercase hover:bg-white hover:text-primary transition-colors duration-300">
            Book a Consultation <ArrowRight size={16} />
          </Link>
        </Reveal>
      </div>
      <Reveal delay={0.1} className="border border-primary-foreground/20 p-8">
        <p className="overline text-accent mb-2">Head Office</p>
        <p className="text-2xl font-display mb-6">{COMPANY.address}<br />{COMPANY.region}</p>
        <p className="text-primary-foreground/70">{COMPANY.email}</p>
        <p className="text-primary-foreground/70">{COMPANY.website}</p>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el) {
          if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
          else el.scrollIntoView({ behavior: "smooth" });
        }
      }, 300);
    } else {
      if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
      else window.scrollTo({ top: 0 });
    }
  }, [location]);

  return (
    <main data-testid="home-page">
      <Hero />
      <MarqueeRibbon />
      <Manifesto />
      <Services />
      <Team />
      <ContactCTA />
      <WhatsAppButton />
    </main>
  );
}
